import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
    ThemeProvider,
    createTheme
} from '@mui/material';

const PromptDialogContext = createContext();

export const usePromptDialog = () => useContext(PromptDialogContext);

export const PromptDialogProvider = ({ children }) => {
    const darkTheme = createTheme({ palette: { mode: 'dark' } });

    const [dialogState, setDialogState] = useState({
        isOpen: false,
        title: '',
        message: '',
        placeholder: '',
        initialValue: '',
        confirmButtonText: 'Confirmar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: 'primary',
        cancelButtonColor: 'inherit',
        maxWidth: 'xs',
        fullWidth: true,
        scroll: 'body',
        pattern: null,
        validate: null,
        inputType: 'text',
        onConfirm: () => {},
        onCancel: () => {},
        children: null
    });
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState('');

    const openPromptDialog = useCallback((options) => {
        setDialogState({
            isOpen: true,
            title: options.title || '',
            message: options.message || '',
            placeholder: options.placeholder || '',
            initialValue: options.initialValue || '',
            confirmButtonText: options.confirmButtonText || 'Confirmar',
            cancelButtonText: options.cancelButtonText || 'Cancelar',
            confirmButtonColor: options.confirmButtonColor || 'primary',
            cancelButtonColor: options.cancelButtonColor || 'inherit',
            maxWidth: options.maxWidth || 'xs',
            fullWidth: options.fullWidth !== undefined ? options.fullWidth : true,
            scroll: options.scroll || 'body',
            pattern: options.pattern || null,
            validate: options.validate || null,
            inputType: options.inputType || 'text',
            onConfirm: options.onConfirm || (() => {}),
            onCancel: options.onCancel || (() => {}),
            children: options.children || null
        });
        setInputValue(options.initialValue || '');
        setError('');
    }, []);

    const closePromptDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, isOpen: false }));
        setError('');
    }, []);

    const handleConfirm = () => {
        // Pattern validation
        if (dialogState.pattern && !new RegExp(dialogState.pattern).test(inputValue)) {
            setError('Formato inválido.');
            return;
        }
        // Callback validation
        if (dialogState.validate && !dialogState.validate(inputValue)) {
            setError('Valor inválido.');
            return;
        }
        dialogState.onConfirm(inputValue);
        closePromptDialog();
    };

    const handleCancel = () => {
        dialogState.onCancel();
        closePromptDialog();
    };

    return (
        <PromptDialogContext.Provider value={{ openPromptDialog, closePromptDialog }}>
            {children}
            <ThemeProvider theme={darkTheme}>
                <Dialog
                    open={dialogState.isOpen}
                    onClose={handleCancel}
                    maxWidth={dialogState.maxWidth}
                    fullWidth={dialogState.fullWidth}
                    scroll={dialogState.scroll}
                >
                    <DialogTitle>{dialogState.title}</DialogTitle>
                    <DialogContent>
                        {dialogState.message && (
                            <DialogContentText>{dialogState.message}</DialogContentText>
                        )}
                        {dialogState.children ? (
                            dialogState.children
                        ) : (
                            <TextField
                                autoFocus
                                margin="dense"
                                type={dialogState.inputType}
                                label={dialogState.placeholder}
                                value={inputValue}
                                onChange={e => {
                                    setInputValue(e.target.value);
                                    setError('');
                                }}
                                error={!!error}
                                helperText={error}
                                fullWidth
                                variant="outlined"
                            />
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            onClick={handleCancel}
                            color={dialogState.cancelButtonColor}
                            variant="outlined"
                        >
                            {dialogState.cancelButtonText}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            color={dialogState.confirmButtonColor}
                            variant="contained"
                            autoFocus
                        >
                            {dialogState.confirmButtonText}
                        </Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </PromptDialogContext.Provider>
    );
};